import { useCallback, useMemo, useState } from 'react'
import { useAtom } from 'jotai'
import { useDebounce } from '@node-space/hooks'
import { ComboboxItem } from '@node-space/ui-components'
import { SEARCH_INPUT_DEBOUNCE_TIME } from 'constants/General'
import { useGetCustomersWalletsQuery } from 'hooks/queries/Customers/useGetCustomersWallets'
import { useSupportedCurrencies } from 'hooks/useSupportedCurrencies'
import { CustomerWalletStatusType } from 'types/customers'
import { convertToWalletAtom } from '../../atoms/atoms'

export const useCustomersWalletsDropdown = (customerId: string) => {
  const [, setConvertToWallet] = useAtom(convertToWalletAtom)
  const { getCustomerWalletOptions } = useSupportedCurrencies()
  const [query, setQuery] = useState('')
  const debouncedQuery: string = useDebounce(query, SEARCH_INPUT_DEBOUNCE_TIME)

  const queryValues = useMemo(
    () => ({
      customerReference: customerId,
      searchTerm: debouncedQuery,
      page: 0,
      size: 100,
      statuses: [CustomerWalletStatusType.ACTIVE],
    }),
    [customerId, debouncedQuery]
  )
  const { data, isFetching } = useGetCustomersWalletsQuery(queryValues)

  const customerWalletOptions: ComboboxItem[] = useMemo(() => {
    if (data?.content) {
      return getCustomerWalletOptions(data.content)
    }
    return []
  }, [data?.content])

  const onCustomerWalletChange = useCallback(
    (walletId: string) => {
      const selectedWallet = data?.content?.find(wallet => wallet.id === walletId)

      if (!selectedWallet) return

      setConvertToWallet({
        id: selectedWallet.id,
        lsid: selectedWallet.id, // for the customer wallet query, the id is the actual lsid, so we repeat it here
        balance: selectedWallet.balance.value,
        description: selectedWallet.name,
        currency: {
          code: selectedWallet.balance.currencyCode,
          pricePrecision: undefined, // the customer wallet query doesn't return the precision yet
        },
      })
      setQuery('')
    },
    [data?.content]
  )

  return {
    isFetching,
    customerWalletOptions,
    onCustomerWalletChange,
    setQuery,
    isEmpty: !isFetching && customerWalletOptions.length === 0,
  }
}
